/* eslint-disable jsx-a11y/anchor-has-content */
import { Trans } from 'react-i18next';
import React, { memo, ReactElement, useCallback } from 'react';

import { defaultDesktopWidth, defaultTabletWidth } from 'constants/onboarding/defaultOnboardingData';
import { getTermsOfUse, getPrivacyPolicy, getRefundPolicy } from 'constants/general/policy';

import { getWindowSize } from 'helpers/styles/stylesUtils';

import { Header, Text, Logo, Image, Button } from 'libComponents';
import LoginButton from 'components/LoginButton';
import headerStyles from 'components/Header/Header.module.css';
import Footer from 'components/Footer';

import commonStyles from 'assets/styles/Common.module.css';
import styles from './Start.module.css';

import logo from 'assets/img/fullLogo.webp';
import mainImage from './img/mainImage.webp';
import start from './img/indexImage.webp';

import { IStartContainerProps } from './types';

const StartContainer = ({ mirrorConfig, toNextPage, startPage }: IStartContainerProps): ReactElement => {
    const isDesktop = getWindowSize(defaultDesktopWidth);
    const isTablet = getWindowSize(defaultTabletWidth);

    const replaceTitle = useCallback(() => {
        if (mirrorConfig?.firstPageTitle) {
            return <Trans>{mirrorConfig.firstPageTitle}</Trans>;
        }

        return <Trans i18nKey="Build muscles in 8 weeks" />;
    }, [mirrorConfig?.firstPageTitle]);

    const replaceImage = useCallback(
        (defaultImage) => {
            if (mirrorConfig?.firstPageImage) {
                return mirrorConfig.firstPageImage;
            }

            return defaultImage;
        },
        [mirrorConfig?.firstPageImage]
    );

    return (
        <>
            <Header
                className={styles.header}
                left={<Logo alt="logo" src={logo} />}
                /* @ts-ignore */
                right={
                    !startPage && (
                        <LoginButton className={headerStyles.logInButtonBox} dataLocator="logInButton">
                            <Text type="overline">
                                <Trans i18nKey="Log in" />
                            </Text>
                        </LoginButton>
                    )
                }
            />
            <div className={styles.wrapper}>
                <div className={styles.container}>
                    <Text
                        className={styles.title}
                        type={isTablet ? 'h3' : 'h4'}
                        center={!isDesktop}
                        mb={isDesktop ? 16 : 8}
                        as="h1"
                    >
                        {replaceTitle()}
                    </Text>
                    <Text type="large-text" color="text-secondary" center mb={isDesktop ? 40 : 38}>
                        <Trans i18nKey="Get a personalized workout plan" />
                    </Text>
                    {!isDesktop && (
                        <Image src={replaceImage(start)} center alt="man" mb={27} className={styles.mainImage} />
                    )}
                    <Text
                        type="caption"
                        color="text-secondary-subdued"
                        center={!isDesktop}
                        mb={24}
                        className={styles.policy}
                    >
                        <Trans
                            i18nKey="startFlow.index.disclaimer"
                            components={{
                                1: (
                                    <a
                                        href={getTermsOfUse()}
                                        rel="noopener noreferrer"
                                        className={commonStyles.colorWhite}
                                        target="_blank"
                                        data-locator="termsOfUseLink"
                                    />
                                ),
                                3: (
                                    <a
                                        href={getPrivacyPolicy()}
                                        rel="noopener noreferrer"
                                        className={commonStyles.colorWhite}
                                        target="_blank"
                                        data-locator="privacyPolicyLink"
                                    />
                                ),
                                5: (
                                    <a
                                        href={getRefundPolicy()}
                                        rel="noopener noreferrer"
                                        className={commonStyles.colorWhite}
                                        target="_blank"
                                        data-locator="refundPolicyLink"
                                    />
                                ),
                            }}
                        />
                    </Text>
                    <div className={styles.buttonWrapper}>
                        <Button dataLocator="startNowBtn" text="Start Now" withPulsating onClick={toNextPage} mb={20} />
                    </div>
                </div>
                {isDesktop && <Image src={mainImage} alt="man" mb={24} className={styles.rightImage} />}
            </div>
            {startPage || <Footer isStartPage />}
        </>
    );
};

export default memo(StartContainer);
